import { useCallback, useEffect, useMemo, useState } from 'react'
import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import { fuelBurn, lapFcyShare, placeFill } from '@racedash/shared'
import type {
  BurnFill,
  BurnLap,
  BurnSession,
  FuelBurn,
  FuelDoc,
  FuelMark,
  FuelMarks,
  FuelPlacement,
  LapDoc,
} from '@racedash/shared'
import { db } from '../lib/firebase'
import { useReplay } from './replayContext'

/**
 * What the organisation says about its fills at an event: which were short,
 * which stretches not to count.
 *
 * Event-wide, because a tank spans sessions, and kept under the organisation
 * like its declarations of who drove — and editable by the same people.
 */
export function useFuelMarks(eventId: string | null, orgId: string | undefined) {
  const [marks, setMarks] = useState<FuelMarks>({})
  const [stops, setStops] = useState<StopFuelAnswers>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setMarks({})
    setStops({})
    setError(null)
    if (!eventId || !orgId) return
    return onSnapshot(
      doc(db, 'events', eventId, 'fuelMarks', orgId),
      (snap) => {
        setMarks((snap.data()?.['fills'] as FuelMarks | undefined) ?? {})
        setStops((snap.data()?.['stops'] as StopFuelAnswers | undefined) ?? {})
      },
      (e) => setError(e.message),
    )
  }, [eventId, orgId])

  /** Marks every fill given — all the rows of one refuel say the same. */
  const mark = useCallback(
    async (fillIds: readonly string[], change: FuelMark) => {
      if (!eventId || !orgId || fillIds.length === 0) return
      await setDoc(
        doc(db, 'events', eventId, 'fuelMarks', orgId),
        { orgId, fills: Object.fromEntries(fillIds.map((id) => [id, change])), updatedAt: serverTimestamp() },
        { merge: true },
      )
    },
    [eventId, orgId],
  )

  /** Says whether a stop took fuel, ahead of the fuel station's figure. */
  const answerStop = useCallback(
    async (session: string, car: string, stopNumber: number, fuelled: boolean) => {
      if (!eventId || !orgId) return
      await setDoc(
        doc(db, 'events', eventId, 'fuelMarks', orgId),
        { orgId, stops: { [session]: { [stopKey(car, stopNumber)]: fuelled } }, updatedAt: serverTimestamp() },
        { merge: true },
      )
    },
    [eventId, orgId],
  )

  return useMemo(() => ({ marks, mark, stops, answerStop, error }), [marks, mark, stops, answerStop, error])
}

/**
 * What the team said of its stops: fuelled or not, by session and then by car
 * and stop number. Only until the fill comes in, which then says for itself.
 */
export type StopFuelAnswers = Record<string, Record<string, boolean>>

export function stopKey(car: string, stopNumber: number): string {
  return `${car}-${stopNumber}`
}

/**
 * One car's fuel across the event: every fill, and every lap of the team's
 * sessions it ran in, worked into stretches from one full tank to the next.
 *
 * The team's sessions only, which is what keeps one race carried on two
 * channels from being counted twice. Live, because the stretch in the car is
 * still growing.
 */
export function useFuelBurn(
  fuel: EventFuelContext | undefined,
  car: string | null,
): { burn: FuelBurn | null; laps: BurnLap[]; loading: boolean; error: string | null } {
  const eventId = fuel?.eventId ?? null
  const marks = fuel?.marks ?? noMarks
  const sessionKey = (fuel?.sessions ?? [])
    .filter((s) => car && s.cars.includes(car))
    .map((s) => `${s.id}@${s.start.getTime()}`)
    .join(',')
  const sessions = useMemo<BurnSession[]>(
    () =>
      sessionKey
        ? sessionKey.split(',').map((k) => {
            const [id, start] = k.split('@')
            return { id: id!, start: new Date(Number(start)) }
          })
        : [],
    [sessionKey],
  )
  const [fills, setFills] = useState<BurnFill[] | null>(null)
  const [laps, setLaps] = useState<Record<string, BurnLap[]>>({})
  const [places, setPlaces] = useState<Record<string, FuelDoc[]>>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setFills(null)
    setError(null)
    if (!eventId || !car) return
    return onSnapshot(
      query(collection(db, 'rawFuel', eventId, 'fills'), where('car', '==', car)),
      (snap) =>
        setFills(
          snap.docs.map((d) => {
            const f = d.data() as { at: { toDate(): Date }; litres: number }
            return { id: d.id, at: f.at.toDate(), litres: f.litres }
          }),
        ),
      (e) => setError(e.message),
    )
  }, [eventId, car])

  useEffect(() => {
    setLaps({})
    setPlaces({})
    if (!car || sessions.length === 0) return
    const unsubs = sessions.map((s) => s.id).flatMap((id) => [
      onSnapshot(
        query(collection(db, 'streamSessions', id, 'laps'), where('car', '==', car)),
        (snap) =>
          setLaps((was) => ({
            ...was,
            [id]: snap.docs.map((d) => {
              const l = d.data() as LapDoc
              return {
                session: id,
                lap: l.lap,
                completedAt: l.completedAt?.toDate() ?? null,
                fcy: lapFcyShare(l),
                lapMs: l.lapTimeMs ?? null,
                pit: !!l.inLap || !!l.outLap,
              }
            }),
          })),
        (e) => setError(e.message),
      ),
      onSnapshot(
        query(collection(db, 'streamSessions', id, 'fuel'), where('car', '==', car)),
        (snap) => setPlaces((was) => ({ ...was, [id]: snap.docs.map((d) => d.data() as FuelDoc) })),
        (e) => setError(e.message),
      ),
    ])
    return () => unsubs.forEach((u) => u())
  }, [car, sessions])

  // During a replay, only what had happened by the replayed instant: the fills
  // and laps after it are not known yet, at this session or any other.
  const replayAt = useReplay()?.at.getTime() ?? null
  const allLaps = useMemo(() => {
    const every = Object.values(laps).flat()
    return replayAt == null ? every : every.filter((l) => l.completedAt != null && l.completedAt.getTime() <= replayAt)
  }, [laps, replayAt])
  const burn = useMemo(() => {
    if (!fills) return null
    const known = replayAt == null ? fills : fills.filter((f) => f.at.getTime() <= replayAt)
    // Every placement the fuel job made of each fill, in the team's sessions.
    const start = new Map(sessions.map((s) => [s.id, s.start]))
    const placements = new Map<string, FuelPlacement[]>()
    for (const [session, docs] of Object.entries(places)) {
      for (const d of docs) {
        if (replayAt != null && d.at.toMillis() > replayAt) continue
        const id = `${d.car}-${fillStamp(d.at.toDate())}`
        const p: FuelPlacement = { session, start: start.get(session) ?? new Date(0), kind: d.kind, lap: d.lap }
        placements.set(id, [...(placements.get(id) ?? []), p])
      }
    }
    const placed = known.map((f) => {
      const place = placeFill(f.at, placements.get(f.id) ?? [])
      return place ? { ...f, place } : f
    })
    return fuelBurn(sessions, allLaps, placed, marks)
  }, [fills, allLaps, places, marks, sessions, replayAt])

  const loading =
    fills === null || sessions.some((s) => laps[s.id] === undefined || places[s.id] === undefined)
  return { burn, laps: allLaps, loading: !!car && !!eventId && loading && !error, error }
}

/**
 * The event's fuel, as the dashboard hands it to the screens: the team's
 * sessions there, and what the team has said about its fills.
 */
export interface EventFuelContext {
  eventId: string
  sessions: { id: string; start: Date; cars: string[] }[]
  marks: FuelMarks
  /** Absent for anyone who may not say who is in the car. */
  mark?: (fillIds: readonly string[], change: FuelMark) => Promise<void>
  /** Whether each stop took fuel, as the team said. */
  stops?: StopFuelAnswers
  /** Absent like `mark`. */
  answerStop?: (session: string, car: string, stopNumber: number, fuelled: boolean) => Promise<void>
}

const noMarks: FuelMarks = {}

/** The fuel job's fill id stamp: UTC, to the second. */
function fillStamp(at: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return (
    `${at.getUTCFullYear()}${p(at.getUTCMonth() + 1)}${p(at.getUTCDate())}-` +
    `${p(at.getUTCHours())}${p(at.getUTCMinutes())}${p(at.getUTCSeconds())}`
  )
}
